import { TaxesAndFeesPriceBreakdown } from './taxes-and-fees-price-breakdown.model';
import { Allowance } from '@rx-applications/shared/data-access';
import { PaymentSummaryCommissionModel } from './payment-summary-commissions.model';
import { PaymentOption } from '@rx-applications/clientsite/shared/config/utils';
import { HotelPriceDetails } from './hotel-pricing-summary.model';
import { CustomFields } from './custom-fields.model';
import { PaymentMethodItem } from './company-payment-methods.model';

export interface Room {
  adults: number;
  currency: string;
  extra: string;
  meal: string;
  name: string;
  price: number;
  reference: string;
  roomRateDescription: string;
  depositRequired: boolean;
  priceBreakdown?: TaxesAndFeesPriceBreakdown;
  inPolicy?: boolean;
  maximumAllocation?: number
}



export interface PaymentMethod {
  allowSaveCreditCard: boolean;
  creditCard: boolean;
  forceNewCreditCard?: boolean; // deprecated
  onAccount: boolean;
}

export interface SupplierPaymentMethod {
  supplierPaidByRoomex: boolean;
  supplierPaidByTraveller: boolean;
  supplierPaymentTime: SupplierPaidByTravellerPaymentTimeEnum;
}

export enum SupplierPaidByTravellerPaymentTimeEnum {
  PaymentTakenBySupplierNow,
  PaymentTakenBySupplierAtProperty,
  NotApplicable,
}

export interface SavedCard {
  ending: string;
  expiry: string;
  id: string;
  name: string;
  type: string;
  isLodgeCard: boolean;
}

export interface BedPreference {
  option: string;
  value: string;
}

export interface CustomerContactDetail {
  address1: string;
  address2: string;
  city: string;
  countryCode: string;
  email: string;
  name: string;
  phone: string;
  postcode: string;
}

export interface TermsAndConditions {
  cancelPenalties: string[];
  contractComments: string[];
  totalPrice: number;
}

export enum CreditCardPaymentType {
  PaidByRoomex,
  PayAtHotel,
  PayInAdvance,
  None,
}

export enum SupplyStatus {
  SUCCESS = 'success',
  SOLDOUT = 'soldout',
  SUPPLIERERROR = 'suppliererror',
}

export interface GetReservationDetailsResponse {
  success: boolean;
  error?: string;
  data: GetReservationDetailsData;
  supplyStatus?: SupplyStatus;
}

export interface GetReservationDetailsData {
  address: string;
  allowanceallowed: boolean;
  allowances: Allowance[];
  approvalRequired?: boolean;
  approverUsername?: string;
  bedPreferences: BedPreference[] | null;
  checkIn: string;
  checkOut: string;
  clientcommission: PaymentSummaryCommissionModel;
  customFields: CustomFields;
  customerContactDetail: CustomerContactDetail;
  description: string;
  freeCancellationBefore: string;
  freeCancellationBeforeWithOffset?: string;
  hasBusinessLeisureQuery: boolean;
  hasSpecialOfferQuery: boolean;
  hasSpecialRequest: boolean;
  hotelId: number;
  hotelName: string;
  locationCode: string;
  numberofnights: number;
  onRequest: boolean;
  outOfPolicy: boolean;
  paymentMethod: PaymentMethod;
  paymentTerm: PaymentOption.PayAdvance | PaymentOption.PayHotel;
  pnrEnabled: boolean;
  priceChange: boolean;
  quoteId?: string;
  rooms: Room[];
  saverplusfee: number;
  stars: number;
  supplierAlias: string;
  supplierPaymentMethod?: SupplierPaymentMethod;
  termsAndConditions: TermsAndConditions;
  thumbnailUrl: string;
  totalprice: number;
}

export interface ReservationDetailsData extends GetReservationDetailsData {
  savedCards: SavedCard[] | PaymentMethodItem[];
  priceDetails?: HotelPriceDetails;
  isOnAccount?: boolean;
  pnr?: string;
  rx3dsId?: string;
}

export class ReservationDetails {
  savedCards: SavedCard[] = [];
  priceDetails?: HotelPriceDetails;
  isOnAccount?: boolean;
  pnr?: string;

  constructor(public getReservationDetailsResponse: GetReservationDetailsResponse) {}

  hasError(): boolean {
    return !this.getReservationDetailsResponse.success;
  }

  isSuccess(): boolean {
    return this.getReservationDetailsResponse.success;
  }

  isSoldOut(): boolean {
    return this.supplyStatus.toLowerCase() === SupplyStatus.SOLDOUT;
  }

  getData(): ReservationDetailsData {
    const { customerContactDetail, customFields } = this.getReservationDetailsResponse.data;
    return {
      ...this.getReservationDetailsResponse.data,
      customFields: {
        ...customFields,
        fields: customFields.fields.filter((field) => field.name !== 'Allowance'),
      },
      customerContactDetail: {
        ...customerContactDetail,
        countryCode: customerContactDetail.countryCode === 'Select Country' ? '' : customerContactDetail.countryCode,
      },
      savedCards: this.savedCards,
    };
  }

  getError(): string | undefined {
    return this.getReservationDetailsResponse.error;
  }

  isPaymentMethodInvalid(): boolean | undefined {
    const { data } = this.getReservationDetailsResponse;
    return (
      !data.paymentMethod.creditCard && data.supplierPaymentMethod && data.supplierPaymentMethod.supplierPaidByTraveller
    );
  }

  isPaymentMethodByCreditCard(): boolean {
    return this.getReservationDetailsResponse.data.paymentMethod.creditCard;
  }

  get supplyStatus(): SupplyStatus {
    return this.getReservationDetailsResponse.supplyStatus || SupplyStatus.SUCCESS;
  }
}
